// Simple apps: the MVP of every app in the suite that doesn't have its own screens yet.
// Each spec is the whole app: its stages, its fields and which fields show in the list. Change a spec and the
// app changes; no migration needed (records keep field values by key, see apps/api/src/simpleApps.ts).
// When an app outgrows this (real BC posting, scheduling logic, a mobile flow), it gets its own screens.

export type FieldType =
  | "text" | "long" | "number" | "money" | "percent" | "date" | "select" | "person" | "yesno"
  | "bc"; // a Business Central key (item, work center, lot…), shown in mono and searchable

export type Field = {
  key: string; label: string; type: FieldType;
  options?: string[]; // select
  bc?: string; // what kind of BC record, e.g. "Item", for bc fields
  hint?: string; required?: boolean; unit?: string;
  maxLength?: number; // text: the most characters it takes (the API takes 4000 at most)
  min?: number; max?: number; // number, money, percent: the lowest and highest it takes
  default?: string; // filled in on a new record
  layer?: "core" | "company"; // company: added by your company (company.ts), on top of the core app
};
export type Stage = { id: string; name: string; tone?: "warn" | "danger" | "ok" | "info" };
export type AppSpec = {
  id: string; // matches the suite module id
  prefix: string; // record numbers, e.g. NCR-00012
  noun: string; // one record, e.g. "nonconformance"
  title: string; titleHint?: string; // what the title field is called
  stages: Stage[]; // first is where new records start; last is done
  fields: Field[];
  columns: string[]; // field keys shown in the list, after number, title and stage ("owner" allowed)
  board?: boolean; // offer the board view (default true)
};

const yes = ["Yes", "No"];
const S = (...names: string[]): Stage[] => names.map((n, i) => ({
  id: n.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""), name: n,
  tone: i === 0 ? "warn" : i === names.length - 1 ? "ok" : "info",
}));

export const APP_SPECS: AppSpec[] = [
  // ----- CRM -----
  { id: "cases", prefix: "CASE", noun: "case", title: "Subject", titleHint: "e.g. Brackets arrived bent",
    stages: S("New", "Investigating", "Waiting on customer", "Resolved", "Closed"),
    fields: [
      { key: "customer", label: "Customer", type: "bc", bc: "Customer", required: true },
      { key: "type", label: "Type", type: "select", options: ["Complaint", "Return (RMA)", "Question", "Warranty"] },
      { key: "salesOrder", label: "Sales order", type: "bc", bc: "Sales order" }, { key: "item", label: "Item", type: "bc", bc: "Item" }, { key: "lot", label: "Lot", type: "bc", bc: "Lot" },
      { key: "qty", label: "Quantity affected", type: "number" },
      { key: "priority", label: "Priority", type: "select", options: ["High", "Medium", "Low"] },
      { key: "description", label: "What the customer said", type: "long" },
      { key: "resolution", label: "Resolution", type: "long" },
    ],
    columns: ["customer", "type", "priority", "owner"] },
  { id: "customer-portal", prefix: "CPA", noun: "portal user", title: "Customer contact", titleHint: "e.g. Jane Ortiz, Pacific Fasteners",
    stages: S("Invited", "Active", "Suspended"),
    fields: [
      { key: "customer", label: "Customer", type: "bc", bc: "Customer", required: true }, { key: "email", label: "Email", type: "text", required: true },
      { key: "seeOrders", label: "Sees orders and ship dates", type: "yesno" }, { key: "seeInvoices", label: "Sees invoices", type: "yesno" },
      { key: "seeCerts", label: "Sees certificates", type: "yesno" }, { key: "canRma", label: "Can request returns", type: "yesno" },
    ],
    columns: ["customer", "email", "owner"], board: false },

  // ----- PLM -----
  { id: "new-items", prefix: "NIR", noun: "new item request", title: "Description", titleHint: "e.g. Bracket, 4-hole, zinc plated",
    stages: S("Request", "Engineering", "Purchasing and costing", "Approved", "Created in BC"),
    fields: [
      { key: "type", label: "Type", type: "select", options: ["Make", "Buy", "Phantom", "Service"] },
      { key: "drawing", label: "Drawing / rev", type: "text" }, { key: "uom", label: "Base unit", type: "select", options: ["PCS", "EA", "LB", "FT", "KG", "M", "BOX"] },
      { key: "itemCategory", label: "Item category", type: "text" }, { key: "vendor", label: "Preferred vendor", type: "bc", bc: "Vendor" },
      { key: "leadTime", label: "Lead time", type: "number", unit: "days" }, { key: "cost", label: "Standard cost", type: "money" },
      { key: "contract", label: "For contract", type: "text" }, { key: "itemNo", label: "BC item no.", type: "bc", bc: "Item", hint: "Filled in once it's created in BC" },
    ],
    columns: ["type", "vendor", "cost", "owner"] },
  { id: "bom-routing", prefix: "BOM", noun: "BOM and routing draft", title: "Parent item", titleHint: "e.g. 2000-ASSY Pump housing",
    stages: S("Draft", "Review", "Approved", "Released to BC"),
    fields: [
      { key: "item", label: "Item", type: "bc", bc: "Item", required: true }, { key: "version", label: "Version", type: "text" },
      { key: "components", label: "Components", type: "long", hint: "One per line: item, quantity, unit" },
      { key: "operations", label: "Operations", type: "long", hint: "One per line: op no., work center, setup, run time" },
      { key: "eco", label: "Change order", type: "text" }, { key: "effective", label: "Effective date", type: "date" },
    ],
    columns: ["item", "version", "effective", "owner"] },
  { id: "npi", prefix: "NPI", noun: "NPI project", title: "Product", titleHint: "e.g. Gen 3 pump housing",
    stages: S("Concept", "Feasibility", "Design", "Validation", "Launch", "In production"),
    fields: [
      { key: "customer", label: "Customer", type: "bc", bc: "Customer" }, { key: "target", label: "Target launch", type: "date" },
      { key: "annualVolume", label: "Annual volume", type: "number" }, { key: "targetCost", label: "Target cost", type: "money" },
      { key: "targetPrice", label: "Target price", type: "money" }, { key: "gateNotes", label: "Gate notes", type: "long" },
      { key: "risk", label: "Risk", type: "select", options: ["Low", "Medium", "High"] },
    ],
    columns: ["customer", "target", "risk", "owner"] },

  // ----- Quality -----
  { id: "ncr", prefix: "NCR", noun: "nonconformance", title: "What's wrong", titleHint: "e.g. Burr on rear edge",
    stages: [{ id: "opened", name: "Opened", tone: "warn" }, { id: "on_hold", name: "Lot on hold", tone: "danger" }, { id: "mrb", name: "MRB review", tone: "info" }, { id: "dispositioned", name: "Dispositioned", tone: "info" }, { id: "closed", name: "Closed", tone: "ok" }],
    fields: [
      { key: "item", label: "Item", type: "bc", bc: "Item", required: true }, { key: "lot", label: "Lot", type: "bc", bc: "Lot" },
      { key: "qty", label: "Quantity", type: "number" }, { key: "source", label: "Found at", type: "select", options: ["Receiving", "In process", "Final inspection", "Customer"] },
      { key: "vendor", label: "Vendor", type: "bc", bc: "Vendor" }, { key: "workCenter", label: "Work center", type: "bc", bc: "Work center" },
      { key: "disposition", label: "Disposition", type: "select", options: ["Use as is", "Rework", "Scrap", "Return to vendor"] },
      { key: "cost", label: "Cost of nonconformance", type: "money" }, { key: "capa", label: "CAPA", type: "text" },
      { key: "description", label: "Details", type: "long" },
    ],
    columns: ["item", "lot", "qty", "disposition"] },
  { id: "inspection", prefix: "INS", noun: "inspection", title: "What's inspected", titleHint: "e.g. Receipt PR-1042, 1000-BRKT",
    stages: S("Pending", "In progress", "Passed", "Failed"),
    fields: [
      { key: "kind", label: "Kind", type: "select", options: ["Receiving", "First article", "In process", "Final"] },
      { key: "item", label: "Item", type: "bc", bc: "Item" }, { key: "lot", label: "Lot", type: "bc", bc: "Lot" },
      { key: "document", label: "Receipt / prod. order", type: "bc", bc: "Document" },
      { key: "sample", label: "Sample size", type: "number" }, { key: "defects", label: "Defects found", type: "number" },
      { key: "results", label: "Measurements", type: "long", hint: "Characteristic, spec, actual" },
    ],
    columns: ["kind", "item", "lot", "defects"] },
  { id: "calibration", prefix: "GAGE", noun: "gauge", title: "Gauge", titleHint: "e.g. 0-6\" digital caliper",
    stages: [{ id: "in_service", name: "In service", tone: "ok" }, { id: "due", name: "Due", tone: "warn" }, { id: "out_for_cal", name: "Out for calibration", tone: "info" }, { id: "out_of_tolerance", name: "Out of tolerance", tone: "danger" }, { id: "retired", name: "Retired" }],
    fields: [
      { key: "serial", label: "Serial no.", type: "text" }, { key: "location", label: "Location", type: "text" },
      { key: "interval", label: "Interval", type: "number", unit: "days" }, { key: "lastCal", label: "Last calibrated", type: "date" },
      { key: "nextDue", label: "Next due", type: "date" }, { key: "provider", label: "Calibrated by", type: "text" },
      { key: "certificate", label: "Certificate no.", type: "text" }, { key: "impact", label: "Out-of-tolerance impact", type: "long" },
    ],
    columns: ["serial", "location", "nextDue", "owner"] },
  { id: "audits", prefix: "AUD", noun: "audit", title: "Audit", titleHint: "e.g. ISO 9001 clause 8.5 internal audit",
    stages: S("Planned", "In progress", "Findings open", "Closed"),
    fields: [
      { key: "type", label: "Type", type: "select", options: ["Internal", "Supplier", "Customer", "Registrar"] },
      { key: "date", label: "Date", type: "date" }, { key: "scope", label: "Scope", type: "long" },
      { key: "vendor", label: "Supplier", type: "bc", bc: "Vendor" },
      { key: "major", label: "Major findings", type: "number" }, { key: "minor", label: "Minor findings", type: "number" },
      { key: "findings", label: "Findings", type: "long", hint: "One per line; each becomes a CAPA if needed" },
    ],
    columns: ["type", "date", "major", "minor"] },
  { id: "fmea", prefix: "FMEA", noun: "FMEA", title: "Process step", titleHint: "e.g. Op 20 CNC drill",
    stages: S("Draft", "Review", "Approved"),
    fields: [
      { key: "item", label: "Item", type: "bc", bc: "Item" }, { key: "routing", label: "Routing / op", type: "text" },
      { key: "failureMode", label: "Failure mode", type: "long" }, { key: "effect", label: "Effect", type: "long" },
      { key: "severity", label: "Severity (1-10)", type: "number" }, { key: "occurrence", label: "Occurrence (1-10)", type: "number" }, { key: "detection", label: "Detection (1-10)", type: "number" },
      { key: "controls", label: "Controls", type: "long" }, { key: "action", label: "Recommended action", type: "long" },
    ],
    columns: ["item", "severity", "occurrence", "detection"], board: false },

  // ----- Supply chain -----
  { id: "scar", prefix: "SCAR", noun: "SCAR", title: "Problem", titleHint: "e.g. Plating thickness below spec",
    stages: S("Issued", "Containment", "Root cause", "Corrective action", "Verified", "Closed"),
    fields: [
      { key: "vendor", label: "Supplier", type: "bc", bc: "Vendor", required: true }, { key: "item", label: "Item", type: "bc", bc: "Item" },
      { key: "po", label: "Purchase order", type: "bc", bc: "Purchase order" }, { key: "ncr", label: "NCR", type: "text" },
      { key: "dueDate", label: "Response due", type: "date" }, { key: "rootCause", label: "Root cause (supplier)", type: "long" },
      { key: "correctiveAction", label: "Corrective action (supplier)", type: "long" },
    ],
    columns: ["vendor", "item", "dueDate", "owner"] },
  { id: "scorecards", prefix: "SC", noun: "scorecard", title: "Supplier and period", titleHint: "e.g. Acme Plating, Q3 2026",
    stages: S("Draft", "Shared with supplier", "Acknowledged"),
    fields: [
      { key: "vendor", label: "Supplier", type: "bc", bc: "Vendor", required: true }, { key: "period", label: "Period", type: "text" },
      { key: "otd", label: "On-time delivery", type: "percent" }, { key: "ppm", label: "Quality (PPM)", type: "number" },
      { key: "responsiveness", label: "Responsiveness (1-5)", type: "number" },
      { key: "grade", label: "Grade", type: "select", options: ["A", "B", "C", "D"] }, { key: "comments", label: "Comments", type: "long" },
    ],
    columns: ["vendor", "period", "otd", "grade"] },

  // ----- Production -----
  { id: "scheduling", prefix: "SCH", noun: "scheduled job", title: "Job", titleHint: "e.g. RPO 101022 op 20",
    stages: S("Unscheduled", "Scheduled", "Running", "Done"),
    fields: [
      { key: "prodOrder", label: "Production order", type: "bc", bc: "Production order", required: true }, { key: "operation", label: "Operation", type: "text" },
      { key: "workCenter", label: "Work center", type: "bc", bc: "Work center" }, { key: "start", label: "Start", type: "date" }, { key: "end", label: "End", type: "date" },
      { key: "hours", label: "Hours", type: "number" }, { key: "sequence", label: "Sequence", type: "number" },
    ],
    columns: ["workCenter", "start", "end", "hours"] },
  { id: "shop-floor", prefix: "JOB", noun: "job report", title: "Job", titleHint: "e.g. RPO 101022 op 20 on CNC-02",
    stages: S("Running", "Reported", "Posted to BC"),
    fields: [
      { key: "prodOrder", label: "Production order", type: "bc", bc: "Production order", required: true }, { key: "operation", label: "Operation", type: "text" },
      { key: "workCenter", label: "Work center", type: "bc", bc: "Work center" }, { key: "operator", label: "Operator", type: "person" },
      { key: "good", label: "Good output", type: "number" }, { key: "scrap", label: "Scrap", type: "number" },
      { key: "scrapReason", label: "Scrap reason", type: "select", options: ["Burr", "Dimension", "Finish", "Material", "Setup", "Other"] },
      { key: "runMinutes", label: "Run time", type: "number", unit: "min" }, { key: "notes", label: "Shift notes", type: "long" },
    ],
    columns: ["workCenter", "operator", "good", "scrap"] },
  { id: "downtime", prefix: "DT", noun: "downtime event", title: "What stopped", titleHint: "e.g. CNC-02 spindle alarm",
    stages: [{ id: "down", name: "Down", tone: "danger" }, { id: "cause_coded", name: "Cause coded", tone: "info" }, { id: "resolved", name: "Resolved", tone: "ok" }],
    fields: [
      { key: "workCenter", label: "Work center", type: "bc", bc: "Work center", required: true },
      { key: "cause", label: "Cause", type: "select", options: ["Breakdown", "Changeover", "No material", "No operator", "Quality hold", "Planned maintenance", "Other"] },
      { key: "started", label: "Started", type: "date" }, { key: "minutes", label: "Minutes lost", type: "number", unit: "min" },
      { key: "workOrder", label: "Maintenance work order", type: "text" }, { key: "notes", label: "Notes", type: "long" },
    ],
    columns: ["workCenter", "cause", "minutes", "owner"] },
  { id: "tier-boards", prefix: "TB", noun: "huddle item", title: "Topic", titleHint: "e.g. Line 3 short on 1000-BRKT",
    stages: [{ id: "open", name: "Open", tone: "warn" }, { id: "in_progress", name: "In progress", tone: "info" }, { id: "escalated", name: "Escalated", tone: "danger" }, { id: "done", name: "Done", tone: "ok" }],
    fields: [
      { key: "tier", label: "Tier", type: "select", options: ["Tier 1 (line)", "Tier 2 (area)", "Tier 3 (plant)"], required: true },
      { key: "area", label: "Line or area", type: "text" },
      { key: "category", label: "Category", type: "select", options: ["Safety", "Quality", "Delivery", "Cost", "People"] },
      { key: "due", label: "Due", type: "date" }, { key: "notes", label: "Notes", type: "long" },
    ],
    columns: ["tier", "area", "category", "due"] },

  // ----- Inventory -----
  { id: "holds", prefix: "HOLD", noun: "hold", title: "What's on hold", titleHint: "e.g. Lot L-2291 suspect plating",
    stages: [{ id: "on_hold", name: "On hold", tone: "danger" }, { id: "mrb_decided", name: "MRB decided", tone: "info" }, { id: "released", name: "Released", tone: "ok" }],
    fields: [
      { key: "item", label: "Item", type: "bc", bc: "Item", required: true }, { key: "lot", label: "Lot", type: "bc", bc: "Lot" },
      { key: "bin", label: "Bin", type: "bc", bc: "Bin" }, { key: "qty", label: "Quantity", type: "number" },
      { key: "reason", label: "Reason", type: "select", options: ["Quality", "Customer complaint", "Supplier issue", "Count variance", "Other"] },
      { key: "ncr", label: "NCR", type: "text" }, { key: "decision", label: "MRB decision", type: "long" },
    ],
    columns: ["item", "lot", "qty", "reason"] },
  // Counts (cycle counts, full counts, quick counts) have their own module: apps/web/src/counts.

  // Maintenance (work orders, PM schedules, assets, spares) has its own module: apps/web/src/maintenance.

  // ----- Safety and EHS -----
  { id: "incidents", prefix: "INC", noun: "incident", title: "What happened", titleHint: "e.g. Forklift near miss at dock 2",
    stages: S("Reported", "Investigated", "Actions", "Closed"),
    fields: [
      { key: "type", label: "Type", type: "select", options: ["Near miss", "First aid", "Recordable", "Lost time", "Property damage"], required: true },
      { key: "date", label: "When", type: "date" }, { key: "location", label: "Where", type: "text" },
      { key: "recordable", label: "OSHA recordable", type: "yesno" }, { key: "daysAway", label: "Days away", type: "number" },
      { key: "rootCause", label: "Root cause", type: "long" }, { key: "actions", label: "Actions", type: "long" },
    ],
    columns: ["type", "date", "location", "recordable"] },
  { id: "safety-audits", prefix: "SI", noun: "safety inspection", title: "Area", titleHint: "e.g. Weld cell weekly walk",
    stages: S("Scheduled", "Done", "Findings open", "Closed"),
    fields: [
      { key: "date", label: "Date", type: "date" }, { key: "checklist", label: "Checklist", type: "select", options: ["Weekly walk", "Forklift", "Fire extinguishers", "Lockout/tagout", "Housekeeping"] },
      { key: "score", label: "Score", type: "percent" }, { key: "findings", label: "Findings", type: "long" },
    ],
    columns: ["date", "checklist", "score", "owner"] },
  { id: "environmental", prefix: "ENV", noun: "environmental record", title: "Record", titleHint: "e.g. SDS: Acetone, Sunnyside",
    stages: [{ id: "current", name: "Current", tone: "ok" }, { id: "review_due", name: "Review due", tone: "warn" }, { id: "expired", name: "Expired", tone: "danger" }],
    fields: [
      { key: "kind", label: "Kind", type: "select", options: ["Safety data sheet", "Waste manifest", "Permit", "Inspection report"], required: true },
      { key: "chemical", label: "Chemical / waste stream", type: "text" }, { key: "location", label: "Where used / stored", type: "text" },
      { key: "expires", label: "Expires / review by", type: "date" }, { key: "reference", label: "Reference no.", type: "text" },
    ],
    columns: ["kind", "location", "expires"] },
  { id: "visitors", prefix: "VIS", noun: "visit", title: "Visitor", titleHint: "e.g. Sam Lee, Acme Electric",
    stages: [{ id: "expected", name: "Expected", tone: "info" }, { id: "on_site", name: "On site", tone: "warn" }, { id: "left", name: "Signed out", tone: "ok" }],
    fields: [
      { key: "company", label: "Company", type: "text" }, { key: "type", label: "Type", type: "select", options: ["Visitor", "Contractor", "Auditor", "Customer", "Delivery"] },
      { key: "host", label: "Host", type: "person" }, { key: "date", label: "Date", type: "date" },
      { key: "briefing", label: "Safety briefing acknowledged", type: "yesno" }, { key: "insurance", label: "Certificate of insurance", type: "yesno" },
    ],
    columns: ["company", "type", "host", "date"] },

  // ----- Documents -----
  { id: "doc-control", prefix: "DOC", noun: "document", title: "Title", titleHint: "e.g. WI-020 CNC setup, bracket family",
    stages: S("Draft", "Review", "Approved", "Effective", "Obsolete"),
    fields: [
      { key: "docNo", label: "Document no.", type: "text", required: true }, { key: "revision", label: "Revision", type: "text" },
      { key: "type", label: "Type", type: "select", options: ["Procedure", "Work instruction", "Form", "Specification", "Policy", "Drawing"] },
      { key: "area", label: "Area", type: "text" }, { key: "approver", label: "Approver", type: "person" },
      { key: "effective", label: "Effective date", type: "date" }, { key: "link", label: "File link", type: "text", hint: "SharePoint or OneDrive link" },
      { key: "changes", label: "What changed", type: "long" },
    ],
    columns: ["docNo", "revision", "type", "effective"] },
  { id: "acknowledgements", prefix: "ACK", noun: "read-and-acknowledge", title: "Document and revision", titleHint: "e.g. WI-020 rev C",
    stages: S("Assigned", "In progress", "Complete"),
    fields: [
      { key: "doc", label: "Document", type: "text", required: true }, { key: "readers", label: "Required readers", type: "long", hint: "Names or roles, one per line" },
      { key: "due", label: "Due", type: "date" }, { key: "done", label: "Acknowledged", type: "number" }, { key: "total", label: "Required", type: "number" },
    ],
    columns: ["doc", "due", "done", "total"] },

  // ----- Training -----
  { id: "skills-matrix", prefix: "SKL", noun: "qualification", title: "Person and skill", titleHint: "e.g. M. Diaz on CNC-02 setup",
    stages: S("Assigned", "Training", "Signed off", "Qualified", "Expired"),
    fields: [
      { key: "employee", label: "Employee", type: "person", required: true }, { key: "workCenter", label: "Work center", type: "bc", bc: "Work center" },
      { key: "operation", label: "Operation / skill", type: "text" }, { key: "level", label: "Level", type: "select", options: ["1 Learning", "2 Can run with help", "3 Independent", "4 Can train others"] },
      { key: "trainer", label: "Trainer", type: "person" }, { key: "expires", label: "Requalify by", type: "date" },
    ],
    columns: ["employee", "workCenter", "level", "expires"] },
  { id: "training-records", prefix: "TR", noun: "training record", title: "Course", titleHint: "e.g. Forklift certification",
    stages: S("Scheduled", "Completed", "Expired"),
    fields: [
      { key: "employee", label: "Employee", type: "person", required: true }, { key: "date", label: "Date", type: "date" },
      { key: "hours", label: "Hours", type: "number" }, { key: "trainer", label: "Trainer", type: "person" },
      { key: "expires", label: "Expires", type: "date" }, { key: "certificate", label: "Certificate link", type: "text" },
    ],
    columns: ["employee", "date", "expires", "hours"] },

  // ----- Improvement -----
  { id: "kaizen", prefix: "KZ", noun: "improvement project", title: "Project", titleHint: "e.g. Cut CNC-02 changeover to 20 minutes",
    stages: S("Idea", "Approved", "Plan", "Do", "Check", "Sustained"),
    fields: [
      { key: "area", label: "Area", type: "text" }, { key: "problem", label: "Problem (current state)", type: "long" },
      { key: "target", label: "Target (future state)", type: "long" }, { key: "rootCause", label: "Root cause", type: "long" },
      { key: "countermeasures", label: "Countermeasures", type: "long" },
      { key: "estSavings", label: "Estimated savings / yr", type: "money" }, { key: "verifiedSavings", label: "Verified savings / yr", type: "money" },
      { key: "due", label: "Due", type: "date" },
    ],
    columns: ["area", "estSavings", "due", "owner"] },
  { id: "suggestions", prefix: "IDEA", noun: "suggestion", title: "Idea", titleHint: "e.g. Shadow board for CNC-02 tools",
    stages: [{ id: "new", name: "New", tone: "warn" }, { id: "reviewed", name: "Reviewed", tone: "info" }, { id: "doing", name: "Doing it", tone: "info" }, { id: "done", name: "Done", tone: "ok" }, { id: "not_now", name: "Not now" }],
    fields: [
      { key: "area", label: "Area", type: "text" }, { key: "submittedBy", label: "Suggested by", type: "person" },
      { key: "benefit", label: "Benefit", type: "select", options: ["Safety", "Quality", "Delivery", "Cost", "Easier work"] },
      { key: "effort", label: "Effort", type: "select", options: ["Just do it", "Small", "Project"] }, { key: "details", label: "Details", type: "long" },
    ],
    columns: ["area", "submittedBy", "benefit", "effort"] },
  { id: "5s", prefix: "FIVES", noun: "5S audit", title: "Area", titleHint: "e.g. Weld cell",
    stages: S("Scheduled", "Scored", "Actions open", "Closed"),
    fields: [
      { key: "date", label: "Date", type: "date" },
      { key: "sort", label: "Sort (1-5)", type: "number" }, { key: "setInOrder", label: "Set in order (1-5)", type: "number" }, { key: "shine", label: "Shine (1-5)", type: "number" },
      { key: "standardize", label: "Standardize (1-5)", type: "number" }, { key: "sustain", label: "Sustain (1-5)", type: "number" },
      { key: "actions", label: "Actions", type: "long" },
    ],
    columns: ["date", "sort", "shine", "sustain"] },

  // ----- Requests and approvals -----
  { id: "approvals", prefix: "REQ", noun: "request", title: "Request", titleHint: "e.g. New torque wrench for line 3",
    stages: [{ id: "draft", name: "Draft", tone: "warn" }, { id: "submitted", name: "Submitted", tone: "info" }, { id: "approved", name: "Approved", tone: "ok" }, { id: "rejected", name: "Rejected", tone: "danger" }, { id: "fulfilled", name: "Fulfilled", tone: "ok" }],
    fields: [
      { key: "type", label: "Type", type: "select", options: ["Capex", "Purchase request", "Price change", "Hire", "Other"], required: true },
      { key: "amount", label: "Amount", type: "money" }, { key: "approver", label: "Approver", type: "person" },
      { key: "needBy", label: "Needed by", type: "date" }, { key: "justification", label: "Why", type: "long" },
      { key: "bcDoc", label: "BC document", type: "bc", bc: "Document", hint: "PO or requisition once created" },
    ],
    columns: ["type", "amount", "approver", "needBy"] },
  { id: "deviations", prefix: "DEV", noun: "deviation", title: "Deviation", titleHint: "e.g. Run 1000-BRKT with alternate finish",
    stages: [{ id: "requested", name: "Requested", tone: "warn" }, { id: "approved", name: "Approved", tone: "info" }, { id: "active", name: "Active", tone: "info" }, { id: "expired", name: "Expired", tone: "ok" }, { id: "rejected", name: "Rejected", tone: "danger" }],
    fields: [
      { key: "item", label: "Item", type: "bc", bc: "Item" }, { key: "spec", label: "Spec deviated from", type: "text" },
      { key: "qtyLimit", label: "Quantity limit", type: "number" }, { key: "expires", label: "Expires", type: "date", required: true },
      { key: "customerApproval", label: "Customer approved", type: "yesno" }, { key: "reason", label: "Reason and risk", type: "long" },
    ],
    columns: ["item", "qtyLimit", "expires", "customerApproval"] },

  // ----- Insights -----
  { id: "kpis", prefix: "KPI", noun: "KPI", title: "Measure", titleHint: "e.g. On-time delivery",
    stages: [{ id: "on_target", name: "On target", tone: "ok" }, { id: "watch", name: "Watch", tone: "warn" }, { id: "off_target", name: "Off target", tone: "danger" }],
    fields: [
      { key: "area", label: "Area", type: "select", options: ["Safety", "Quality", "Delivery", "Cost", "People"] },
      { key: "unit", label: "Unit", type: "select", options: ["%", "PPM", "$", "Hours", "Count", "Days"] },
      { key: "target", label: "Target", type: "number" }, { key: "actual", label: "Latest actual", type: "number" },
      { key: "period", label: "Period", type: "text" }, { key: "source", label: "Source", type: "text", hint: "Where the number comes from" },
    ],
    columns: ["area", "target", "actual", "period"] },
  { id: "agents", prefix: "EXC", noun: "exception", title: "What needs a decision", titleHint: "e.g. PO 106020 ships 9 days late",
    stages: [{ id: "needs_decision", name: "Needs decision", tone: "warn" }, { id: "approved", name: "Approved", tone: "ok" }, { id: "rejected", name: "Rejected", tone: "danger" }, { id: "auto", name: "Done by agent", tone: "info" }],
    fields: [
      { key: "agent", label: "Agent", type: "select", options: ["Planning", "Supplier portal", "Quality", "Maintenance", "Sales"] },
      { key: "record", label: "Record", type: "bc", bc: "Document" }, { key: "proposal", label: "What the agent proposes", type: "long" },
      { key: "impact", label: "Impact", type: "money" },
    ],
    columns: ["agent", "record", "impact"] },

  // ----- Admin -----
  { id: "users", prefix: "USR", noun: "user", title: "Person", titleHint: "e.g. Maria Diaz",
    stages: [{ id: "invited", name: "Invited", tone: "warn" }, { id: "active", name: "Active", tone: "ok" }, { id: "disabled", name: "Disabled" }],
    fields: [
      { key: "email", label: "Email", type: "text", required: true },
      { key: "role", label: "Role", type: "select", options: ["Admin", "Manager", "Engineer", "Quality", "Buyer", "Planner", "Operator", "Viewer"] },
      { key: "site", label: "Site", type: "text" }, { key: "areas", label: "Areas", type: "text", hint: "e.g. Quality, Production" },
    ],
    columns: ["email", "role", "site"], board: false },
];

// Your company's layer (company.ts): its own fields after the core ones, and its changes to core fields and list columns.
import { COMPANY_FIELDS, COMPANY_OVERRIDES } from "./company";
for (const spec of APP_SPECS) {
  for (const [key, o] of Object.entries(COMPANY_OVERRIDES[spec.id] ?? {})) {
    const f = spec.fields.find((x) => x.key === key);
    if (!f) continue;
    const { inList, ...rest } = o;
    Object.assign(f, rest);
    if (inList === true && !spec.columns.includes(key)) spec.columns.push(key);
    if (inList === false) spec.columns = spec.columns.filter((c) => c !== key);
  }
  for (const f of COMPANY_FIELDS[spec.id] ?? []) if (!spec.fields.some((x) => x.key === f.key)) spec.fields.push({ ...f, layer: "company" });
}

export const findSpec = (id: string) => APP_SPECS.find((s) => s.id === id) ?? null;
